"use client";

import React from "react";
import { Mail, Phone, MapPin, Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function SyncForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-md bg-secondary/20 flex items-center justify-center border border-secondary/30">
          <svg className="size-8 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold text-foreground">
            Design Request Received
          </h3>
          <p className="text-muted-foreground max-w-sm mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-md border-primary/50 hover:bg-primary/10">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground/80">Project Name</FormLabel>
              <FormControl>
                <Input placeholder="E.g. Solitaire Engagement Ring" {...field} className="bg-background/30 border-border/40 focus-visible:border-secondary rounded-md" />
              </FormControl>
              <FormDescription className="text-xs font-mono opacity-60">Reference for your CAD model</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground/80">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="designer@studio.com" {...field} className="bg-background/30 border-border/40 focus-visible:border-secondary rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground/80">Service Type</FormLabel>
              <FormControl>
                <Input placeholder="CAD Design or 3D Printing" {...field} className="bg-background/30 border-border/40 focus-visible:border-secondary rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground/80">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="bg-background/30 border-border/40 focus-visible:border-secondary rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please provide a brief description"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground/80">Project Description & Specifications</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your vision. Mention if you need .3dm, .stl, or .jpg renderings..." className="min-h-[120px] bg-background/30 border-border/40 focus-visible:border-secondary resize-y rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-semibold group bg-gradient-to-r from-secondary to-primary text-secondary-foreground rounded-md shadow-lg hover:shadow-secondary/20 transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Submit Request
            <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
          </>}
      </Button>
    </Form>;
}
export default function SyncWithUs() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223960" id="request-a-quote" className="bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-[0.05]"></div>
        {}
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-secondary/10 rounded-md blur-[120px] opacity-40 mix-blend-screen"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-primary/10 rounded-md blur-[100px] opacity-20 mix-blend-screen"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-5 space-y-10 transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-card/30 border border-border/40 backdrop-blur-md">
                <span className="relative flex size-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-md bg-secondary opacity-75"></span>
                  <span className="relative inline-flex rounded-md size-2.5 bg-secondary"></span>
                </span>
                <span className="text-xs font-mono font-medium text-muted-foreground tracking-widest uppercase">
                  Studio Status: Online
                </span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tighter text-foreground leading-[1.1]">
                Transforming <br className="hidden lg:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary">
                  Vision to CAD.
                </span>
              </h2>
              
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed font-default">
                Expert Jewelry CAD design and high-precision 3D printing. We deliver production-ready .3dm and .stl files with photorealistic .jpg renderings.
              </p>
            </div>

            <div className="space-y-6 pt-4 border-t border-border/20">
              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-md bg-card border border-border/40 text-secondary group-hover:scale-110 group-hover:bg-secondary/10 transition-all duration-300 shadow-sm">
                  <Mail className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-mono text-muted-foreground mb-1">Direct Inquiry</p>
                  <p className="text-base font-default font-medium text-foreground">hdemirtshyan93@gmail.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-md bg-card border border-border/40 text-secondary group-hover:scale-110 group-hover:bg-secondary/10 transition-all duration-300 shadow-sm">
                  <Phone className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-mono text-muted-foreground mb-1">Studio Line</p>
                  <p className="text-base font-default font-medium text-foreground">+1 (617) 843-6352</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-md bg-card border border-border/40 text-secondary group-hover:scale-110 group-hover:bg-secondary/10 transition-all duration-300 shadow-sm">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-mono text-muted-foreground mb-1">Design Lab</p>
                  <p className="text-base font-default font-medium text-foreground">Jewelry District, Boston 02108</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative group">
              {}
              <div className="absolute -inset-[1px] rounded-md bg-gradient-to-b from-secondary/40 via-primary/20 to-transparent opacity-40 group-hover:opacity-80 transition-opacity duration-500 blur-[1px]"></div>
              
              <div className="relative bg-card/40 backdrop-blur-xl border border-border/40 rounded-md p-6 sm:p-8 md:p-10 shadow-2xl overflow-hidden">
                {}
                <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/5 rounded-md blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                
                <div className="mb-8">
                  <h3 className="text-2xl font-default font-semibold text-foreground mb-2">
                    Request a Quote
                  </h3>
                  <p className="text-sm text-muted-foreground font-default">
                    Upload your sketches or describe your project. Our engineering team will respond with a detailed estimate within 24 hours.
                  </p>
                </div>

                <FormProvider formKey="request_quote_start_your_project" formId="request_quote_start_your_project" sectionName="request-a-quote" defaultValues={{
                name: "",
                email: "",
                company: "",
                phone: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="Submit Request" successMessage="Your project specifications have been received. We are reviewing your requirements and will provide a quote for your jewelry CAD or 3D printing job shortly.">
                  <SyncForm />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}